/**
 * Game State Reducer
 * 
 * Implements Redux-style state management pattern using React's useReducer.
 * This provides predictable state updates and makes the application easier to debug.
 * 
 * Educational Purpose:
 * - Demonstrates the reducer pattern for state management
 * - Shows how to handle complex state transitions
 * - Provides a single source of truth for game state
 * 
 * Why Reducer Pattern?
 * - Predictable: Same action + same state = same result
 * - Testable: Pure functions are easy to test
 * - Debuggable: Action history can be logged/replayed
 * - Scalable: Easy to add new actions without modifying existing code
 */

import { GameState, GameAction } from '@core/types';
import { GameEngine } from '@core/GameEngine';
import { getLevel } from '@core/levels';

/**
 * Initial game state
 * Used when the app first loads or when resetting the game
 */
export const initialGameState: GameState = {
  currentRoom: 0,
  rooms: new Map(),
  edges: new Map(),
  visited: new Set([0]),
  pdaStack: [],
  gameStatus: 'intro',
  solutionPath: [],
  currentLevel: 1,
  timeElapsed: 0,
  bestTimes: {},
  isTutorialMode: false,
  currentTutorialStep: 0,
  moveHistory: [],
  hintsRemaining: 3,
  showHint: false,
};

/**
 * Game state reducer
 * 
 * Processes actions and returns new state. This function must be pure:
 * - No side effects
 * - Same inputs always produce same outputs
 * - Does not mutate the original state
 * 
 * @param state - Current game state
 * @param action - Action to process
 * @returns New game state
 */
export const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'START_INTRO': {
      return {
        ...initialGameState,
        gameStatus: 'intro',
      };
    }

    case 'START_TUTORIAL': {
      // Start tutorial mode with a simple 4-room graph
      const engine = new GameEngine(4);
      const graph = engine.getGraph();

      return {
        ...state,
        currentRoom: 0,
        rooms: graph.getRooms(),
        edges: graph.getEdges(),
        visited: new Set([0]),
        pdaStack: [],
        gameStatus: 'tutorial',
        solutionPath: graph.getSolutionPath(),
        currentLevel: 0, // Tutorial is level 0
        timeElapsed: 0,
        isTutorialMode: true,
        currentTutorialStep: 0,
        moveHistory: [0],
        hintsRemaining: 3,
        showHint: false,
      };
    }

    case 'START_LEVEL': {
      // Initialize a level with specified number of rooms
      const { level, isTutorial = false, resetTimer = true } = action.payload;
      const levelConfig = getLevel(level);
      
      if (!levelConfig) {
        return state;
      }

      const engine = new GameEngine(levelConfig.numRooms);
      const graph = engine.getGraph();

      return {
        ...state,
        currentRoom: 0,
        rooms: graph.getRooms(),
        edges: graph.getEdges(),
        visited: new Set([0]),
        pdaStack: [],
        gameStatus: 'playing',
        solutionPath: graph.getSolutionPath(),
        currentLevel: level,
        timeElapsed: resetTimer ? 0 : state.timeElapsed, // Keep timer if retrying
        isTutorialMode: isTutorial,
        currentTutorialStep: 0,
        moveHistory: [0],
        hintsRemaining: isTutorial ? 999 : 3, // Unlimited hints in tutorial
        showHint: false,
      };
    }

    case 'MOVE_TO_ROOM': {
      const { roomId } = action.payload;

      // Validate move
      const currentEdges = state.edges.get(state.currentRoom) ?? [];
      const isValidMove = currentEdges.includes(roomId) && !state.visited.has(roomId);

      if (!isValidMove) {
        // Invalid move - return state unchanged
        return state;
      }

      // Create new visited set with the new room
      const newVisited = new Set(state.visited);
      newVisited.add(roomId);

      // Update PDA stack
      const newPdaStack = [...state.pdaStack, `move-${state.currentRoom}->${roomId}`];

      // Update move history
      const newMoveHistory = [...state.moveHistory, roomId];

      // Determine new game status
      let newStatus: GameState['gameStatus'] = state.gameStatus;
      const totalRooms = state.rooms.size;
      let newBestTimes = state.bestTimes;

      if (newVisited.size === totalRooms) {
        // All rooms visited - level complete!
        newStatus = state.isTutorialMode ? 'tutorial' : 'level_complete';
        
        // Save best time for this level (not in tutorial)
        if (!state.isTutorialMode) {
          const currentBest = state.bestTimes[state.currentLevel];
          newBestTimes = {
            ...state.bestTimes,
            [state.currentLevel]: currentBest
              ? Math.min(currentBest, state.timeElapsed)
              : state.timeElapsed,
          };
        }
      } else {
        // Check if player has any available moves from new room
        const newRoomEdges = state.edges.get(roomId) ?? [];
        const availableMoves = newRoomEdges.filter(id => !newVisited.has(id));

        if (availableMoves.length === 0) {
          // No moves available and not all rooms visited - player loses
          newStatus = 'lost';
        }
      }

      return {
        ...state,
        currentRoom: roomId,
        visited: newVisited,
        pdaStack: newPdaStack,
        gameStatus: newStatus,
        bestTimes: newBestTimes,
        moveHistory: newMoveHistory,
        showHint: false, // Hide hint after move
      };
    }

    case 'UNDO_MOVE': {
      // Only allow undo in tutorial mode or if there's history
      if (state.moveHistory.length <= 1) {
        return state; // Can't undo from starting position
      }

      // Get previous room from history
      const newMoveHistory = state.moveHistory.slice(0, -1);
      const previousRoom = newMoveHistory[newMoveHistory.length - 1];

      // Type guard - should never be undefined due to length check above
      if (previousRoom === undefined) {
        return state;
      }

      // Rebuild visited set from move history
      const newVisited = new Set(newMoveHistory);

      // Remove last PDA stack entry
      const newPdaStack = state.pdaStack.slice(0, -1);

      return {
        ...state,
        currentRoom: previousRoom,
        visited: newVisited,
        pdaStack: newPdaStack,
        moveHistory: newMoveHistory,
        gameStatus: state.isTutorialMode ? 'tutorial' : 'playing',
        showHint: false,
      };
    }

    case 'NEXT_TUTORIAL_STEP': {
      // Advance to next tutorial step
      return {
        ...state,
        currentTutorialStep: state.currentTutorialStep + 1,
        showHint: false,
      };
    }

    case 'USE_HINT': {
      // Show hint and decrement counter (unless in tutorial with unlimited)
      if (state.hintsRemaining <= 0 && !state.isTutorialMode) {
        return state;
      }

      return {
        ...state,
        showHint: true,
        hintsRemaining: state.isTutorialMode ? state.hintsRemaining : state.hintsRemaining - 1,
      };
    }

    case 'HIDE_HINT': {
      return {
        ...state,
        showHint: false,
      };
    }

    case 'TICK_TIMER': {
      // Increment timer by 1 second (continues even when lost, stops when level complete)
      if (state.gameStatus === 'playing' || state.gameStatus === 'lost') {
        return {
          ...state,
          timeElapsed: state.timeElapsed + 1,
        };
      }
      return state;
    }

    case 'COMPLETE_LEVEL': {
      // Mark level as complete and save best time
      const currentBest = state.bestTimes[state.currentLevel];
      const newBestTimes = {
        ...state.bestTimes,
        [state.currentLevel]: currentBest
          ? Math.min(currentBest, state.timeElapsed)
          : state.timeElapsed,
      };

      return {
        ...state,
        gameStatus: 'level_complete',
        bestTimes: newBestTimes,
      };
    }

    case 'NEXT_LEVEL': {
      // Move to next level
      return {
        ...state,
        currentLevel: state.currentLevel + 1,
        gameStatus: 'idle',
      };
    }

    case 'RESET_GAME': {
      // Return to intro screen
      return {
        ...initialGameState,
        bestTimes: state.bestTimes, // Preserve best times
        gameStatus: 'idle',
      };
    }

    case 'UPDATE_PDA_STACK': {
      // Manually update PDA stack (for advanced features)
      const { move } = action.payload;
      return {
        ...state,
        pdaStack: [...state.pdaStack, move],
      };
    }

    default:
      // Unknown action - return state unchanged
      // TypeScript's exhaustive checking ensures we handle all action types
      return state;
  }
};

/**
 * Helper function to create action creators
 * These functions make it easier to dispatch actions with proper typing
 */
export const gameActions = {
  startIntro: (): GameAction => ({
    type: 'START_INTRO',
  }),

  startTutorial: (): GameAction => ({
    type: 'START_TUTORIAL',
  }),

  startLevel: (level: number, isTutorial = false, resetTimer = true): GameAction => ({
    type: 'START_LEVEL',
    payload: { level, isTutorial, resetTimer },
  }),

  moveToRoom: (roomId: number): GameAction => ({
    type: 'MOVE_TO_ROOM',
    payload: { roomId },
  }),

  undoMove: (): GameAction => ({
    type: 'UNDO_MOVE',
  }),

  resetGame: (): GameAction => ({
    type: 'RESET_GAME',
  }),

  updatePDAStack: (move: string): GameAction => ({
    type: 'UPDATE_PDA_STACK',
    payload: { move },
  }),

  tickTimer: (): GameAction => ({
    type: 'TICK_TIMER',
  }),

  completeLevel: (): GameAction => ({
    type: 'COMPLETE_LEVEL',
  }),

  nextLevel: (): GameAction => ({
    type: 'NEXT_LEVEL',
  }),

  nextTutorialStep: (): GameAction => ({
    type: 'NEXT_TUTORIAL_STEP',
  }),

  useHint: (): GameAction => ({
    type: 'USE_HINT',
  }),

  hideHint: (): GameAction => ({
    type: 'HIDE_HINT',
  }),
};